import { FormEvent, useEffect, useMemo, useState } from "react";
import Modal from "../ui/Modal";
import SelectField from "../controls/SelectField";
import MultiSelectField from "../controls/MultiSelectField";
import { BedDouble, Building2, Globe, Home, Info, MoonStar, Users } from "lucide-react";
import {
  AdminHotelCreateInput,
  AdminHotelRow,
  AdminHotelUpdateInput,
  HotelType,
} from "../../types/hotels";
import { SCompanyRow } from "../../types/companies";
import {
  createAdminHotel,
  updateAdminHotel,
} from "../../api/hotels";
import { OtaKey } from "../../types/ota";
import OtaUrlsFields, {
  buildOtaUrlsPayload,
  countFilledOtaUrls,
  EMPTY_OTA_VALUES,
  getInvalidOtaKeys,
  normalizeOtaValues,
  OtaValues,
} from "../ota/OtaUrlsFields";
import {
  buildGuestSelectionOptions,
  splitGuestSelections,
  toGuestSelectionValues,
} from "../../utils/guests";

const TYPE_OPTIONS: HotelType[] = [
  "Отель",
  "Сетевой отель",
  "Апартаменты",
  "Гостевой дом",
  "Коттедж, вилла",
];
const MIN_STAY_OPTIONS = Array.from({ length: 10 }, (_, index) => index + 1);
const GUEST_OPTIONS = [1, 2];
const OBJECTS_LIMIT_REACHED_MESSAGE = "Достигнут лимит объектов размещения для компании";

type Mode = "create" | "edit";

type Props = {
  open: boolean;
  mode: Mode;
  onClose: () => void;
  onSuccess: (hotel: AdminHotelRow) => void;
  companies: SCompanyRow[];
  initialHotel?: AdminHotelRow | null;
};

export default function AdminHotelModal({
  open,
  mode,
  onClose,
  onSuccess,
  companies,
  initialHotel,
}: Props) {
  const [companyId, setCompanyId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [type, setType] = useState<HotelType | "">("");
  const [otaCount, setOtaCount] = useState<string>("");
  const [otaUrls, setOtaUrls] = useState<OtaValues>(EMPTY_OTA_VALUES);
  const [minStay, setMinStay] = useState<string[]>([]);
  const [minStayTouched, setMinStayTouched] = useState(false);
  const [guests, setGuests] = useState<string[]>([]);
  const [guestsTouched, setGuestsTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const isEdit = mode === "edit";
  const parsedOtaLimit = /^\d+$/.test(otaCount.trim()) ? Number(otaCount) : null;
  const filledOtaCount = useMemo(() => countFilledOtaUrls(otaUrls), [otaUrls]);
  const isOverOtaLimit = parsedOtaLimit !== null && filledOtaCount > parsedOtaLimit;
  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60 disabled:opacity-60 disabled:cursor-not-allowed";

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialHotel) {
      setCompanyId(String(initialHotel.company_id));
      setName(initialHotel.name);
      setType(initialHotel.type);
      setOtaCount(String(initialHotel.ota_count ?? 3));
      setOtaUrls(normalizeOtaValues(initialHotel.ota_urls, initialHotel.url));
      setMinStay((initialHotel.min_stay ?? []).map(String));
      setGuests(toGuestSelectionValues(initialHotel.guests, initialHotel.guests_child));
    } else {
      setCompanyId(initialHotel?.company_id ? String(initialHotel.company_id) : "");
      setName("");
      setType("");
      setOtaCount(initialHotel?.company_id ? String(companies.find((company) => company.id === initialHotel.company_id)?.ota_count ?? 3) : "");
      setOtaUrls(EMPTY_OTA_VALUES);
      setMinStay([]);
      setGuests([]);
    }
    setMinStayTouched(false);
    setGuestsTouched(false);
    setError("");
    setSubmitting(false);
  }, [open, mode, initialHotel, companies]);

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        label: company.company_name,
        value: String(company.id),
      })),
    [companies],
  );

  const typeOptions = useMemo(
    () => TYPE_OPTIONS.map((option) => ({ label: option, value: option })),
    [],
  );
  const minStayOptions = useMemo(
    () => MIN_STAY_OPTIONS.map((option) => ({ label: String(option), value: String(option) })),
    [],
  );
  const guestOptions = useMemo(
    () => buildGuestSelectionOptions(GUEST_OPTIONS, null, { includeAllChildOptions: true }),
    [],
  );

  function normalizeMinStaySelection(value: string[]) {
    const allowedValues = new Set(MIN_STAY_OPTIONS.map(String));
    return value
      .filter((item) => allowedValues.has(item))
      .sort((left, right) => Number(left) - Number(right));
  }

  function normalizeGuestsSelection(value: string[]) {
    const allowedValues = new Set(guestOptions.map((option) => option.value));
    return value
      .filter((item) => allowedValues.has(item))
      .sort((left, right) => {
        const leftIndex = guestOptions.findIndex((option) => option.value === left);
        const rightIndex = guestOptions.findIndex((option) => option.value === right);
        return leftIndex - rightIndex;
      });
  }

  function handleCompanyChange(value: string) {
    setCompanyId(value);
    if (mode === "create") {
      const selectedCompany = companies.find((company) => String(company.id) === value);
      setOtaCount(String(selectedCompany?.ota_count ?? 3));
    }
  }

  function handleOtaUrlChange(key: OtaKey, value: string) {
    setOtaUrls((current) => ({
      ...current,
      [key]: value,
    }));
    setError("");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError("");

    const trimmedName = name.trim();
    const invalidOtaFields = getInvalidOtaKeys(otaUrls);

    if (!companyId) {
      return setError("Выберите компанию");
    }
    if (!trimmedName) {
      return setError("Введите название");
    }
    if (trimmedName.length > 150) {
      return setError("Название не может превышать 150 символов");
    }
    if (!type) {
      return setError("Выберите тип");
    }
    if (!/^\d+$/.test(otaCount.trim())) {
      return setError("Укажите целое число для количества ОТА");
    }
    if (Number(otaCount) < 1) {
      return setError("Количество ОТА должно быть не меньше 1");
    }
    if (invalidOtaFields.size > 0) {
      return setError("Проверьте ссылки ОР: одна или несколько ссылок не соответствуют примеру");
    }
    if (!isEdit && filledOtaCount === 0) {
      return setError("Добавьте хотя бы одну OTA-ссылку для объекта размещения");
    }
    if (isOverOtaLimit) {
      return setError(`Превышен лимит OTA-ссылок для объекта размещения: доступно ${otaCount}, указано ${filledOtaCount}`);
    }

    setSubmitting(true);
    const otaUrlsPayload = buildOtaUrlsPayload(otaUrls);

    const basePayload = {
      name: trimmedName,
      type: type as HotelType,
      ota_count: Number(otaCount),
      ota_urls: otaUrlsPayload,
    };

    try {
      if (mode === "create") {
        const payload: AdminHotelCreateInput = {
          ...basePayload,
          company_id: Number(companyId),
        };
        const created = await createAdminHotel(payload);
        onSuccess(created);
      } else if (initialHotel) {
        const payload: AdminHotelUpdateInput = {
          ...basePayload,
        };
        if (Number(companyId) !== initialHotel.company_id) {
          payload.company_id = Number(companyId);
        }
        if (minStayTouched) {
          payload.min_stay = minStay.length ? minStay.map(Number) : null;
        }
        if (guestsTouched) {
          const parsedGuests = splitGuestSelections(guests);
          payload.guests = parsedGuests.guests;
          payload.guests_child = parsedGuests.guests_child;
        }
        const updated = await updateAdminHotel(initialHotel.id, payload);
        onSuccess(updated);
      }
      onClose();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setError(e?.response?.data?.detail || OBJECTS_LIMIT_REACHED_MESSAGE);
      } else {
        setError(e?.response?.data?.detail || "Не удалось сохранить изменения");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title=""
      widthClass="max-w-5xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="flex items-start gap-4 animate-softFade">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner sm:h-14 sm:w-14">
            <Home size={22} />
          </div>
          <div className="min-w-0">
            <h3 className="text-xl font-semibold text-slate-900 sm:text-2xl">
              {mode === "create" ? "Добавить объект размещения" : "Редактировать объект размещения"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {isEdit
                ? "Обновите основные параметры, min stay и доступные варианты гостей"
                : "Свяжите объект с компанией и укажите параметры маппинга"}
            </p>
          </div>
        </div>

        <div className={isEdit ? "grid grid-cols-1 gap-4 md:grid-cols-2" : "space-y-4"}>
          <label className="block space-y-2 md:col-span-1">
            <span className="text-sm font-semibold text-slate-600">Компания *</span>
            <SelectField
              value={companyId}
              onChange={(value) => handleCompanyChange(String(value))}
              options={companyOptions}
              placeholder="Выберите компанию"
              icon={<Building2 size={18} />}
              className="border-slate-200/80 bg-white/80 shadow-inner"
              disabled={submitting}
            />
          </label>

          <label className="block space-y-2 md:col-span-1">
            <span className="text-sm font-semibold text-slate-600">Тип *</span>
            <SelectField
              value={type}
              onChange={(value) => setType(value as HotelType)}
              options={typeOptions}
              placeholder="Выберите тип"
              icon={<BedDouble size={18} />}
              className="border-slate-200/80 bg-white/80 shadow-inner"
              disabled={submitting}
            />
          </label>

          <label className="block space-y-2 md:col-span-1">
            <span className="text-sm font-semibold text-slate-600">Название *</span>
            <div className="relative">
              <Building2
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, TravelStat Hotel"
                value={name}
                maxLength={150}
                onChange={(event) => setName(event.target.value)}
                disabled={submitting}
              />
            </div>
          </label>

          <label className="block space-y-2 md:col-span-1">
            <span className="text-sm font-semibold text-slate-600">Количество ОТА *</span>
            <div className="relative">
              <Globe
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="number"
                min={1}
                step={1}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="3"
                value={otaCount}
                onChange={(event) => setOtaCount(event.target.value)}
                disabled={submitting}
              />
            </div>
          </label>

          <div className="md:col-span-2">
            <OtaUrlsFields
              values={otaUrls}
              onChange={handleOtaUrlChange}
              disabled={submitting}
              officialLabel="Сайт отеля"
              title={isEdit ? "Маппинг" : "Маппинг *"}
            />
          </div>

          {parsedOtaLimit !== null ? (
            <div className="md:col-span-2 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
              Максимум ОТА для объекта: {parsedOtaLimit}
            </div>
          ) : null}

          {isOverOtaLimit ? (
            <div className="md:col-span-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              Превышен лимит OTA-ссылок для объекта размещения: разрешено {parsedOtaLimit}, указано {filledOtaCount}.
            </div>
          ) : null}

          {isEdit && (
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">Мин. stay</span>
              <MultiSelectField
                value={minStay}
                onChange={(value) => {
                  setMinStayTouched(true);
                  setMinStay(normalizeMinStaySelection(value));
                }}
                options={minStayOptions}
                placeholder="Не задан"
                icon={<MoonStar size={18} />}
                className="border-slate-200/80 bg-white/80 shadow-inner"
                disabled={submitting}
              />
            </label>
          )}

          {isEdit && (
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">Гостей</span>
              <MultiSelectField
                value={guests}
                onChange={(value) => {
                  setGuestsTouched(true);
                  setGuests(normalizeGuestsSelection(value));
                }}
                options={guestOptions}
                placeholder="Гостей"
                icon={<Users size={18} />}
                className="border-slate-200/80 bg-white/80 shadow-inner"
                disabled={submitting}
              />
            </label>
          )}

          {isEdit && (
            <div className="md:col-span-2 flex gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
              <Info size={18} className="mt-0.5 shrink-0" />
              <p>
                Данные настройки нужны только для отображения исторических данных с парсингов которых уже нет. Если вы хотите настроить реальный парсинг, это делается в настройках парсинга
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            disabled={submitting || isOverOtaLimit}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none"
          >
            {submitting ? "Сохраняем…" : mode === "create" ? "Добавить" : "Сохранить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
